import {
  LogoutOutlined,
  SettingOutlined,
  UserOutlined,
} from '@ant-design/icons';
import { history, useModel } from '@umijs/max';
import type { MenuProps } from 'antd';
import { Spin } from 'antd';
import { createStyles } from 'antd-style';
import React, { useEffect, useState } from 'react';
import { flushSync } from 'react-dom';
import {
  fetchAvailableApps,
  fetchUserPermissions,
  normalizePermissionCodes,
  recordAppSwitchLog,
} from '@/services/sd/user';
import { userCache } from '@/store/userInfo';
import { clearActiveApiKey } from '@/utils/apiKey';
import { sanitizeRememberedAppScopes } from '@/utils/appScope';
import { logoutToLogin } from '@/utils/session';
import HeaderDropdown from '../HeaderDropdown';

export type GlobalHeaderRightProps = {
  menu?: boolean;
  children?: React.ReactNode;
};

export const AvatarName = () => {
  const { initialState } = useModel('@@initialState');
  const { currentUser } = initialState || {};
  const [, setVersion] = useState(0);

  useEffect(() => {
    const handler = () => setVersion((v) => v + 1);
    window.addEventListener('user-cache-update', handler);
    return () => window.removeEventListener('user-cache-update', handler);
  }, []);

  const name = userCache.userName || currentUser?.name || '';

  return (
    <span
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        margin: 0,
        marginLeft: -8,
      }}
    >
      {name}
    </span>
  );
};

const useStyles = createStyles(({ token }) => ({
  action: {
    display: 'flex',
    height: '48px',
    marginLeft: 'auto',
    overflow: 'hidden',
    alignItems: 'center',
    padding: '0 8px',
    cursor: 'pointer',
    borderRadius: token.borderRadius,
    '&:hover': {
      backgroundColor: token.colorBgTextHover,
    },
  },
}));

export const AvatarDropdown: React.FC<GlobalHeaderRightProps> = ({
  menu,
  children,
}) => {
  const { styles } = useStyles();
  const [, setVersion] = useState(0);

  useEffect(() => {
    const handler = () => setVersion((v) => v + 1);
    window.addEventListener('user-cache-update', handler);
    return () => window.removeEventListener('user-cache-update', handler);
  }, []);

  const { initialState, setInitialState } = useModel('@@initialState');
  const [syncingApps, setSyncingApps] = useState(false);

  const loginOut = async () => {
    logoutToLogin({ setInitialState });
  };

  const syncPermissionsByApp = async (appUuid: string) => {
    if (!appUuid || userCache.isSuperAdmin === 1) {
      userCache.permissions = [];
      userCache.permissionAppUuid = '';
      return;
    }
    try {
      const permRes = await fetchUserPermissions(appUuid);
      userCache.permissions = normalizePermissionCodes(permRes, appUuid);
      userCache.permissionAppUuid = appUuid;
    } catch {
      userCache.permissions = [];
      userCache.permissionAppUuid = '';
    }
  };

  const refreshSwitchableApps = async () => {
    if (userCache.isSuperAdmin === 1 || syncingApps) return;
    setSyncingApps(true);
    try {
      const list = (await fetchAvailableApps(
        userCache.guid || undefined,
      )) as Array<{ uuid: string; name?: string }>;
      userCache.apps = list;

      const prevCurrent = userCache.currentAppUuid || '';
      const nextCurrent = list.some((app) => app.uuid === prevCurrent)
        ? prevCurrent
        : list[0]?.uuid || '';
      userCache.currentAppUuid = nextCurrent;
      sanitizeRememberedAppScopes(
        list.map((app) => app.uuid),
        nextCurrent || '',
      );

      clearActiveApiKey();
      await syncPermissionsByApp(nextCurrent);
      window.dispatchEvent(new Event('user-cache-update'));

      if (prevCurrent && nextCurrent && prevCurrent !== nextCurrent) {
        window.location.reload();
      }
      if (prevCurrent && !nextCurrent) {
        history.replace('/overview');
      }
    } finally {
      setSyncingApps(false);
    }
  };

  const onMenuClick: MenuProps['onClick'] = async (event) => {
    const { key } = event;

    if (typeof key === 'string' && key.startsWith('app:')) {
      if (userCache.isSuperAdmin === 1) return;

      const appUuid = key.replace('app:', '');
      if (appUuid && userCache.currentAppUuid !== appUuid) {
        userCache.currentAppUuid = appUuid;
        clearActiveApiKey();
        await syncPermissionsByApp(appUuid);
        try {
          await recordAppSwitchLog(appUuid);
        } catch (error) {
          console.warn('Failed to record app switch log', error);
        }
        window.location.reload();
      }
      return;
    }

    if (key === 'logout') {
      flushSync(() => {
        setInitialState((s) => ({ ...s, currentUser: undefined }));
      });
      loginOut();
      return;
    }

    history.push(`/account/${key}`);
  };

  const loading = (
    <span className={styles.action}>
      <Spin size="small" style={{ marginLeft: 8, marginRight: 8 }} />
    </span>
  );

  if (!initialState) return loading;

  const { currentUser } = initialState;
  const displayName = userCache.userName || currentUser?.name;
  if (!displayName) return loading;

  type AppMenuItem = {
    uuid: string;
    name?: string;
    appCode?: string;
  };

  const apps: AppMenuItem[] = Array.isArray(userCache.apps)
    ? userCache.apps
    : [];
  const currentApp = apps.find((app) => app.uuid === userCache.currentAppUuid);
  const isSuperAdmin = userCache.isSuperAdmin === 1;
  const appItems =
    !isSuperAdmin && apps.length > 0
      ? [
          {
            key: 'app-current',
            label: `当前应用: ${currentApp?.name || currentApp?.appCode || '未选择'}`,
            disabled: true,
          },
          { type: 'divider' as const },
          {
            key: 'app-group',
            type: 'group' as const,
            label: '切换应用',
            children: apps.map((app) => ({
              key: `app:${app.uuid}`,
              label: app.name || app.appCode || app.uuid,
            })),
          },
          { type: 'divider' as const },
        ]
      : [];

  const menuItems = [
    ...appItems,
    ...(menu
      ? [
          { key: 'center', icon: <UserOutlined />, label: '个人中心' },
          { key: 'settings', icon: <SettingOutlined />, label: '个人设置' },
          { type: 'divider' as const },
        ]
      : []),
    { key: 'logout', icon: <LogoutOutlined />, label: '退出登录' },
  ];

  return (
    <HeaderDropdown
      onOpenChange={(open) => {
        if (open) {
          void refreshSwitchableApps();
        }
      }}
      menu={{
        selectedKeys: [],
        onClick: onMenuClick,
        items: menuItems,
      }}
    >
      {children}
    </HeaderDropdown>
  );
};
